import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users as UsersIcon, Plus, ArrowRight, AlertCircle, Star } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, userFullName, userRoleLabel, userRoleColor } from "@/lib/labels";

const ROLE_VALUES = ["admin", "amministrazione", "back_office", "seller"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", password: "", role_default: "back_office", is_seller: false, must_change_password: false });

  const load = async () => {
    try {
      const r = await api.get(`/users?include_inactive=${includeInactive}`);
      setUsers(r.data);
      const m = await api.get("/admin/migration-review");
      setPendingCount(m.data.length);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const create = async () => {
    if (form.password.length < 10) { toast.error("Password: min 10 caratteri"); return; }
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false); setForm({ email: "", first_name: "", last_name: "", password: "", role_default: "back_office", is_seller: false, must_change_password: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <UsersIcon className="h-6 w-6 text-blue-600" /> Utenti
        </h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} /> Includi disattivati
          </label>
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="user-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo utente
          </Button>
        </div>
      </div>

      {pendingCount > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">
              {pendingCount} {pendingCount === 1 ? "utente migrato" : "utenti migrati"} dallo Sprint 0 sono disattivati.
            </div>
            <div className="text-amber-800 mt-0.5">Rivedi ciascuno e assegna il ruolo corretto, poi attivali.</div>
          </div>
          <Link to="/admin/migration-review">
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-900 hover:bg-amber-100">Rivedi</Button>
          </Link>
        </Card>
      )}

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Nome</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Ruolo</th>
                <th className="px-4 py-2.5">Venditore</th>
                <th className="px-4 py-2.5">Attivo</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 ${!u.is_active ? "bg-slate-50/60 text-slate-500" : ""}`}>
                  <td className="px-4 py-3 font-medium">{userFullName(u)}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${userRoleColor(u)}`}>{userRoleLabel(u)}</span>
                  </td>
                  <td className="px-4 py-3">{u.is_seller ? <Star className="h-4 w-4 text-amber-500 fill-amber-400" /> : "—"}</td>
                  <td className="px-4 py-3">
                    {u.is_active
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">Disattivato</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/users/${u.id}`} className="text-blue-600 hover:underline text-xs flex items-center justify-end gap-1" data-testid={`user-row-${u.id}`}>
                      Apri <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nessun utente</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Nome *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="user-input-first" /></div>
              <div><Label className="text-xs">Cognome</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-input-email" /></div>
            <div><Label className="text-xs">Password * (min 10, 1 maiuscola, 1 minuscola, 1 numero)</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-input-password" /></div>
            <div><Label className="text-xs">Ruolo *</Label>
              <Select value={form.role_default} onValueChange={(v) => setForm({ ...form, role_default: v })}>
                <SelectTrigger data-testid="user-input-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_VALUES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_seller} onCheckedChange={(v) => setForm({ ...form, is_seller: v })} /> È venditore
            </label>
            <label className="flex items-center gap-2 text-sm pt-2 border-t">
              <Switch
                checked={form.must_change_password}
                onCheckedChange={(v) => setForm({ ...form, must_change_password: v })}
                data-testid="user-input-must-change"
              />
              Richiedi cambio password al primo accesso
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="user-create-submit">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
